import React, { useState, useMemo } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { PermitLetter, LetterGeneration, Employee } from '../../types';
import PersonnelLetterForm from '../../components/HR/PersonnelLetterForm';
import PersonnelLetterGenerator from '../../components/HR/PersonnelLetterGenerator';
import { useEmployees } from '../../hooks/useEmployees';
import { usePermitLetters, useCreatePermitLetter, useUpdatePermitLetter, useDeletePermitLetter } from '../../hooks/usePermitLetters';
import { useLetterGenerations, useCreateLetterGeneration, useDeleteLetterGeneration } from '../../hooks/useLetterGenerations';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFiles } from '../../services/upload.service';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';

const PersonnelLetters: React.FC = () => {
  const { data: apiEmployees = [] } = useEmployees({ populate: '*' });
  const employees = apiEmployees as Employee[];

  const queryOptions = useMemo(() => ({ populate: '*' }), []);
  const { data: allPermitLetters = [], isLoading, error, refetch } = usePermitLetters(queryOptions);
  const letters = useMemo(() => allPermitLetters.filter(l => l.type === 'personnel'), [allPermitLetters]);

  const createMutation = useCreatePermitLetter(() => refetch());
  const updateMutation = useUpdatePermitLetter(() => refetch());
  const deleteMutation = useDeletePermitLetter(() => refetch());

  const { user } = useAuth();
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.username || 'admin';

  const generationQueryOptions = useMemo(() => ({ populate: '*' }), []);
  const { data: allGenerations = [], refetch: refetchGenerations } = useLetterGenerations(generationQueryOptions);
  const createGenerationMutation = useCreateLetterGeneration(() => refetchGenerations());
  const deleteGenerationMutation = useDeleteLetterGeneration(() => refetchGenerations());
  const generations = useMemo(
    () => allGenerations.filter(g => g.letterType === 'personnel'),
    [allGenerations]
  );
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState<PermitLetter | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'templates' | 'generations'>('templates');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createdByFilter, setCreatedByFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const uniqueCreators = Array.from(new Set(letters.map(letter => letter.createdBy).filter(Boolean)));

  const filteredLetters = useMemo(() => {
    return letters.filter(letter => {
      const matchesSearch = !searchTerm || (
        letter.templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        letter.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        letter.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        letter.createdBy.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesType = typeFilter === 'all' || (
        (typeFilter === 'default' && letter.isDefault) ||
        (typeFilter === 'custom' && !letter.isDefault)
      );
      const matchesCreatedBy = createdByFilter === 'all' || letter.createdBy === createdByFilter;
      const createdDate = new Date(letter.createdAt);
      const matchesDateFrom = !dateFromFilter || createdDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || createdDate <= new Date(dateToFilter);
      return matchesSearch && matchesType && matchesCreatedBy && matchesDateFrom && matchesDateTo;
    });
  }, [letters, searchTerm, typeFilter, createdByFilter, dateFromFilter, dateToFilter]);

  const filteredGenerations = useMemo(() => {
    return generations.filter(generation => {
      const letter = letters.find(l => l.id === generation.letterId);
      const matchesSearch = !searchTerm || (
        letter?.templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        generation.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        generation.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        generation.generatedBy.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || generation.status === statusFilter;
      const generatedDate = new Date(generation.generatedDate);
      const matchesDateFrom = !dateFromFilter || generatedDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || generatedDate <= new Date(dateToFilter);
      return matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo;
    });
  }, [generations, letters, searchTerm, statusFilter, dateFromFilter, dateToFilter]);

  const letterExportData = useMemo(() => filteredLetters.map(l => ({
    'Template Name': l.templateName, Subject: l.subject, Type: l.isDefault ? 'Default' : 'Custom',
    'Created By': l.createdBy, 'Created Date': new Date(l.createdAt).toLocaleDateString(),
    'Updated Date': new Date(l.updatedAt).toLocaleDateString(), Content: l.content.substring(0, 100) + '...'
  })), [filteredLetters]);
  const genExportData = useMemo(() => filteredGenerations.map(g => {
    const letter = letters.find(l => l.id === g.letterId);
    const names = g.selectedItems.map(id => employees.find(e => e.id === id)?.name || 'Unknown').join(', ');
    return { 'Letter Template': letter?.templateName || 'Unknown', 'Generated By': g.generatedBy,
      'Generated Date': new Date(g.generatedDate).toLocaleDateString(), 'Employee Count': g.selectedItems.length,
      Employees: names, Status: g.status, Notes: g.notes || '', Recipients: g.recipients?.join(', ') || '' };
  }), [filteredGenerations, letters, employees]);
  const handleExportLetters = () => exportToExcel(letterExportData, `personnel_letters_${new Date().toISOString().split('T')[0]}`);
  const handlePrintLetters = () => printAsExcel(letterExportData, 'Personnel Letter Templates');
  const handleExportGenerations = () => exportToExcel(genExportData, `personnel_generations_${new Date().toISOString().split('T')[0]}`);
  const handlePrintGenerations = () => printAsExcel(genExportData, 'Personnel Letter Generations');

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setStatusFilter('all');
    setCreatedByFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const letterColumns: TableColumn[] = [
    { key: 'templateName', label: 'Template Name', sortable: true },
    { key: 'subject', label: 'Subject', sortable: true },
    { key: 'isDefault', label: 'Type', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
        {value ? 'Default' : 'Custom'}
      </span>
    )},
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() }
  ];

  const openGeneratedLetter = (generation: LetterGeneration) => {
    if (!generation.printHtml) return;
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(generation.printHtml);
      w.document.close();
    }
  };

  const generationColumns: TableColumn[] = [
    { key: 'letterId', label: 'Template', sortable: true, render: (value: string) => {
      const letter = letters.find(l => String(l.id) === String(value) || (l as any).documentId === value);
      return letter?.templateName || 'Unknown';
    }},
    { key: 'printHtml', label: 'Letter', sortable: false, render: (_: string, item: LetterGeneration) => (
      item.printHtml ? (
        <button
          onClick={() => openGeneratedLetter(item)}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          View / Print
        </button>
      ) : <span className="text-gray-400">-</span>
    )},
    { key: 'selectedItems', label: 'Employees', sortable: false, render: (value: string[]) => {
      const employeeNames = value.map(id => {
        const employee = employees.find(e => e.id === id);
        return employee?.name || 'Unknown';
      }).join(', ');
      return employeeNames.length > 50 ? `${employeeNames.substring(0, 50)}...` : employeeNames;
    }},
    { key: 'generatedDate', label: 'Generated', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'generatedBy', label: 'Generated By', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value === 'sent' ? 'bg-green-100 text-green-800' :
        value === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {value.charAt(0).toUpperCase() + value.slice(1)}
      </span>
    )}
  ];

  const templateFilterConfigs = [
    { key: 'type', label: 'Type', type: 'select' as const, value: typeFilter, onChange: setTypeFilter, options: [
      { value: 'all', label: 'All Types' }, { value: 'default', label: 'Default' }, { value: 'custom', label: 'Custom' }
    ]},
    { key: 'createdBy', label: 'Created By', type: 'select' as const, value: createdByFilter, onChange: setCreatedByFilter, options: [
      { value: 'all', label: 'All Creators' }, ...uniqueCreators.map(c => ({ value: c, label: c }))
    ]},
    { key: 'dateFrom', label: 'From Date', type: 'date' as const, value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'To Date', type: 'date' as const, value: dateToFilter, onChange: setDateToFilter }
  ];

  const generationFilterConfigs = [
    { key: 'status', label: 'Status', type: 'select' as const, value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'All Statuses' }, { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'pending', label: 'Pending' }
    ]},
    { key: 'dateFrom', label: 'From Date', type: 'date' as const, value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'To Date', type: 'date' as const, value: dateToFilter, onChange: setDateToFilter }
  ];

  const handleEditLetter = (letter: PermitLetter) => { setSelectedLetter(letter); setIsFormOpen(true); };
  const handleDeleteLetter = async (letter: PermitLetter) => {
    if (window.confirm(`Are you sure you want to delete the template "${letter.templateName}"?`)) {
      try { await deleteMutation.execute(letter); } catch { /* Error handled by hook toast */ }
    }
  };
  const handleAddLetter = () => { setSelectedLetter(undefined); setIsFormOpen(true); };
  const handleGenerateLetter = () => setIsGeneratorOpen(true);

  const handleDeleteGeneration = async (generation: LetterGeneration) => {
    const letter = letters.find(l => String(l.id) === String(generation.letterId) || (l as any).documentId === generation.letterId);
    if (window.confirm(`Are you sure you want to delete the letter generated on ${new Date(generation.generatedDate).toLocaleDateString()}${letter ? ` using "${letter.templateName}"` : ''}?`)) {
      try { await deleteGenerationMutation.execute(generation); } catch { /* Error handled by hook toast */ }
    }
  };

  const handleSubmitLetter = async (data: Partial<PermitLetter> & { logoFile?: File }) => {
    try {
      const { logoFile, ...letterData } = data;
      if (logoFile instanceof File) {
        try {
          const uploaded = await uploadFiles([logoFile]);
          if (uploaded[0]?.url) letterData.logoUrl = uploaded[0].url;
        } catch { /* toast handled by hook; keep saving other fields */ }
      }
      const createdBy = letterData.createdBy || selectedLetter?.createdBy || userName;
      if (selectedLetter) {
        await updateMutation.execute({ id: selectedLetter.id, data: { ...letterData, type: 'personnel', documentId: (selectedLetter as any).documentId, createdBy } });
      } else {
        await createMutation.execute({ ...letterData, type: 'personnel', createdBy });
      }
    } catch { /* Error handled by hook toast */ }
    setIsFormOpen(false);
    setSelectedLetter(undefined);
  };

  const handleSubmitGeneration = async (data: Partial<LetterGeneration>) => {
    try {
      await createGenerationMutation.execute({
        letterId: data.letterId || '',
        letterType: 'personnel',
        selectedItems: data.selectedItems || [],
        generatedDate: new Date().toISOString(),
        generatedBy: userName,
        status: 'draft',
        customContent: data.customContent,
        recipients: data.recipients,
        notes: data.notes,
        branchId: data.branchId,
        printHtml: data.printHtml
      });
    } catch { /* Error handled by hook toast */ }
    setIsGeneratorOpen(false);
  };

  const handleCancel = () => { setIsFormOpen(false); setIsGeneratorOpen(false); setSelectedLetter(undefined); };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  if (error) return <ErrorState message={error.message} onRetry={refetch} entityName="personnel letters" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Personnel Letters Management"
        description="Manage letter templates and generate letters for multiple employees"
        actions={[{ label: 'Generate Letter', onClick: handleGenerateLetter, variant: 'primary' }]}
      />
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Letter Templates
          </button>
          <button
            onClick={() => setActiveTab('generations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'generations' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Generated Letters
          </button>
        </nav>
      </div>
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={activeTab === 'templates' ? 'Search templates...' : 'Search generations...'}
        filters={activeTab === 'templates' ? templateFilterConfigs : generationFilterConfigs}
        onClearFilters={clearFilters}
        totalCount={activeTab === 'templates' ? letters.length : generations.length}
        filteredCount={activeTab === 'templates' ? filteredLetters.length : filteredGenerations.length}
        entityName={activeTab === 'templates' ? 'Letter Templates' : 'Generated Letters'}
        onAdd={activeTab === 'templates' ? handleAddLetter : undefined}
        addLabel="Add Template"
        onExport={activeTab === 'templates' ? handleExportLetters : handleExportGenerations}
        onPrint={activeTab === 'templates' ? handlePrintLetters : handlePrintGenerations}
      />
      {activeTab === 'templates' ? (
        <Table
          columns={letterColumns}
          data={filteredLetters}
          onEdit={handleEditLetter}
          onDelete={handleDeleteLetter}
          title="Letter Templates"
          showAddButton={false}
          searchable={false}
        />
      ) : (
        <Table
          columns={generationColumns}
          data={filteredGenerations}
          onDelete={handleDeleteGeneration}
          title="Generated Letters"
          showAddButton={false}
          showEditButton={false}
          searchable={false}
        />
      )}
      {isFormOpen && (
        <PersonnelLetterForm letter={selectedLetter} onSubmit={handleSubmitLetter} onCancel={handleCancel} />
      )}
      {isGeneratorOpen && (
        <PersonnelLetterGenerator letters={letters} employees={employees} onSubmit={handleSubmitGeneration} onCancel={handleCancel} />
      )}
    </div>
  );
};

export default PersonnelLetters;
